import React, { useRef, useState } from 'react'
import { Box } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import { isEditorContentEmpty } from 'components/CommonComponents/RichTextEditor/utils'
import { ImageDescription } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Widgets/Image/ImageDescription'
import { ReferenceMediaSection } from 'components/TasksV2/TaskCreation/components/ReferenceMediaSection'
import { MediaUploadActions, mediaUploadLiterals } from 'components/TasksV2/TaskCreation/components/MediaUploadActions'
import DraggableMediaGallery from 'components/TasksV2/UI/DraggableMediaGallery'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { usePromptReferenceImages } from '../../hooks/usePromptReferenceImages'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import useLocaleText from 'components/useLocaleText'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import { useAILoadingContext } from 'components/TasksV2/AITaskCreation/AIFormStepContext'

interface IAIDescriptionSectionProps {
  subtaskPath: string
}

export const AIDescriptionSection: React.FC<IAIDescriptionSectionProps> = ({ subtaskPath }) => {
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)

  const { control, watch, setValue, getValues } = useFormContext()
  const { setIsMediaUploading } = useFormStepContext()
  const { selectedSubtaskType } = useAILoadingContext()
  const isQuestion = selectedSubtaskType === 'question'
  const imageInputRef = useRef<HTMLInputElement>(null)

  const uploadText = useLocaleText(mediaUploadLiterals)
  const t_step2_placeholder = useLocaleText('step2_placeholder')
  const t_confirm_title = useLocaleText('card_confirm_text')
  const t_yes = useLocaleText('yes')
  const t_no = useLocaleText('no')

  const imagesPath = `${subtaskPath}.content.images`
  const richTextPath = `${subtaskPath}.content.rich_text`
  const formImages: string[] = watch(imagesPath) || []
  const richText = watch(richTextPath)

  const hasContent = formImages.length > 0 || !isEditorContentEmpty(richText)

  const { isUploading, uploadingUrls, uploadImages } = usePromptReferenceImages({
    readImages: () => (getValues(imagesPath) as string[]) || [],
    writeImages: (images) => setValue(imagesPath, images),
    onBusyChange: setIsMediaUploading,
  })

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith('image/'))
    event.target.value = ''
    await uploadImages(files)
  }

  const handleRemoveMedia = (index: number) => {
    setValue(
      imagesPath,
      formImages.filter((_, i) => i !== index)
    )
  }

  const handleReorderMedia = (startIndex: number, endIndex: number) => {
    const reordered = [...formImages]
    const [moved] = reordered.splice(startIndex, 1)
    reordered.splice(endIndex, 0, moved)
    setValue(imagesPath, reordered)
  }

  const handleDropMedia = async (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'))
    if (!imageFiles.length) return
    await uploadImages(imageFiles)
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleDropMedia,
    accept: 'image/*',
  })

  const handleDeleteContent = () => {
    setValue(richTextPath, null)
    setValue(imagesPath, [])
    setIsConfirmDeleteOpen(false)
  }

  return (
    <>
      <Box pos="relative">
        <ImageDescription
          subtaskPath={subtaskPath}
          placeholder={t_step2_placeholder}
          showClear={hasContent}
          onClear={() => setIsConfirmDeleteOpen(true)}
        />

        {!isQuestion && (
          <Box mt="14px">
            <DropzoneWrapper
              onDrop={onDrop}
              onPaste={handlePaste}
              disabled={isUploading}
              role="group"
              aria-label={uploadText.dropImagesHint}
            >
              <ReferenceMediaSection>
                <MediaUploadActions
                  disabled={isUploading}
                  onImage={() => imageInputRef.current?.click()}
                  onCapture={(file) => {
                    void uploadImages([file])
                  }}
                />
                <DraggableMediaGallery
                  layout="grid"
                  uploadingUrls={uploadingUrls}
                  attachedImages={formImages}
                  isShowRemoveIcon
                  isDragDisabled={isUploading}
                  onRemoveMedia={handleRemoveMedia}
                  onReorder={handleReorderMedia}
                />
              </ReferenceMediaSection>
            </DropzoneWrapper>
          </Box>
        )}

        <HiddenFileInput ref={imageInputRef} onChange={handleImageUpload} accept="image/*" multiple />
        <Controller name={imagesPath} control={control} defaultValue={[]} as={<Box />} />
      </Box>

      <ConfirmationPopup
        isOpen={isConfirmDeleteOpen}
        onConfirm={handleDeleteContent}
        onCancel={() => setIsConfirmDeleteOpen(false)}
        title={t_confirm_title}
        description=""
        confirmButtonText={t_yes}
        cancelButtonText={t_no}
        isLoading={false}
      />
    </>
  )
}
